<template>
  <TransactionListTemplate
    :screenConfig="setScreenConfig"
    :rowClick="rowClickCallBack"
    :setLoading="setLoading"
    :ref="'distribution-manifest-list'"
    :class="'distribution-manifest-list'"
    :persistenceKey="getPersistenceKey"
    :key="listKey"
  />
</template>

<script>
import RouteMixin from "@/_mixins/route.mixin";
import FormMixin from "@/_mixins/form.mixin";
import DataGridMixin from "@/_mixins/data-grid.mixin";

import TransactionListTemplate from "../../../templates/transaction-list";
import { FeatureStorage } from "../../../_services/storage";
import {
  DistributionManifestListTabs,
  GetIndexByTabName,
  DistributionManifestTabFilters,
  GetTabTextByTabName,
} from "../../../_models/tabdata/sales/distribution-manifest-tabs";
import ColumnWidth from "../../../_helpers/data-grid/ColumnWidth";

export default {
  props: {},
  mixins: [RouteMixin, FormMixin, DataGridMixin],
  components: {
    TransactionListTemplate,
  },
  data() {
    return {
      selectedTabId: "all",
      setLoading: false,
      listKey: 0,
    };
  },
  computed: {
    setScreenConfig() {
      const selectedFilter =
        DistributionManifestTabFilters[this.selectedTabId] || "";

      return {
        title: "Distribution Manifest",
        ScreenName:
          "Distribution Manifest - " + GetTabTextByTabName(this.selectedTabId),
        exportFileName: "Distribution Manifest List",
        selectedIndex: GetIndexByTabName(this.selectedTabId),
        tabs: DistributionManifestListTabs(),
        gridData: {
          gridOptions: {
            dataSource: {
              endpoint: "DistributionManifest",
              filter: selectedFilter,
              serverPaging: true,
              allowGrouping: false,
              postProcessCallback: (data) => {
                return data.map((item) => {
                  // D (Draft), P (Planned), B (Booked), S (Despatched), C (Cancelled)
                  if (item.Status === "D") {
                    item.Status = "Draft";
                  } else if (item.Status === "P") {
                    item.Status = "Planned";
                  } else if (item.Status === "B") {
                    item.Status = "Booked";
                  } else if (item.Status === "S") {
                    item.Status = "Despatched";
                  } else if (item.Status === "C") {
                    item.Status = "Cancelled";
                  }

                  item.TotalGrossWeight = Math.ceil(item.TotalGrossWeight);
                  return item;
                });
              },
            },
            noDataMessage: {
              addNew: {
                class: "add-new",
                title: "No Manifests",
                message: "Get Started, Create a Distribution Manifest",
              },
            },
          },
          toolbar: {
            search: false,
            columnChooser: true,
            export: true,
            openFilter: true,
            expand: false,
            customButtons: [
              {
                icon: "plus",
                label: "Add New",
                clickAction: this.addNewManifest,
                buttonLocation: "left",
                visible: FeatureStorage.checkPermission(
                  "DistributionManifest",
                  "create",
                ),
              },
            ],
          },
          columns: [
            {
              screenField: "Code",
              gridDataField: "ManifestId",
              filterDataField: "ManifestId",
              label: "Manifest ID",
              filterLabel: "Manifest ID",
              maxWidth: ColumnWidth.SalesOrderNumber.width + 15,
              fixed: true,
              fixedPosition: "left",
              alignment: "left",
              allowHiding: false,
              sortIndex: 0,
              sortOrder: "desc",
              filterIndex: 0,
              type: "Integer",
            },
            {
              screenField: "ItemDescription",
              label: "Carrier",
              gridDataField: "CarrierSupplierCode",
              type: "Text",
              cellTemplate: "Record",
              cellTemplateRecord: {
                primary: [
                  {
                    dataField: "CarrierSupplierName",
                  },
                ],
                secondary: [
                  {
                    dataField: "CarrierSupplierCode",
                  },
                ],
              },
              exportFields: [
                {
                  dataField: "CarrierSupplierCode",
                  label: "Carrier Code",
                },
                {
                  dataField: "CarrierSupplierName",
                  label: "Carrier Name",
                },
              ],
            },
            {
              screenField: "FreightCarrier",
              filterLabel: "Carrier",
              filterDataField: "CarrierSupplierCode",
              filterIndex: 2,
              visible: false,
              showInColumnChooser: false,
              allowExport: false,
            },
            {
              dataField: "RunReference",
              label: "Run Reference",
              filterLabel: "Run Reference",
              filterDataField: "RunReference",
              filterOperator: "contains",
              minWidth: 150,
              width: "auto",
            },
            {
              screenField: "DateIn",
              dataField: "PlannedDispatchDate",
              label: "Planned Dispatch",
              type: "Date",
              filterLabel: "Planned Dispatch",
              filterDataField: "PlannedDispatchDate",
              filterIndex: 3,
              minWidth: ColumnWidth.Date.minWidth + 30,
              width: ColumnWidth.Date.width + 30,
            },
            {
              dataField: "TotalItems",
              label: "Total Items",
              alignment: "right",
              allowSearch: false,
              minWidth: ColumnWidth.Quantity4DecimalPoints.minWidth,
              width: ColumnWidth.Quantity4DecimalPoints.width,
              format: "###,###,##0.0000",
              cellTemplate: "Decimal",
              visible: false,
            },
            // Pallets column should be removed from both the grid and the Column Chooser —
            // the underlying calculation logic doesn't correctly account for Layers/Stacked,
            // so it shouldn't be surfaced here until that logic is fixed
            // (tracked separately if needed)
            // {
            //   dataField: "TotalPallets",
            //   label: "Pallets",
            //   alignment: "right",
            //   allowSearch: false,
            //   minWidth: ColumnWidth.Quantity4DecimalPoints.minWidth,
            //   width: ColumnWidth.Quantity4DecimalPoints.width,
            //   format: "###,###,##0",
            //   cellTemplate: "Decimal",
            // },
            {
              dataField: "TotalSpaces",
              label: "Spaces",
              alignment: "right",
              allowSearch: false,
              minWidth: ColumnWidth.Quantity4DecimalPoints.minWidth,
              width: ColumnWidth.Quantity4DecimalPoints.width,
              format: "###,###,##0",
              cellTemplate: "Decimal",
            },
            {
              dataField: "TotalGrossWeight",
              label: "Gross Weight (kg)",
              alignment: "right",
              allowSearch: false,
              minWidth: ColumnWidth.Quantity4DecimalPoints.minWidth,
              width: ColumnWidth.Quantity4DecimalPoints.width,
              format: "###,###,##0",
              cellTemplate: "Decimal",
            },

            {
              screenField: "Status",
              label: "Status",
              gridDataField: "Status",
              filterLabel: "Status",
              filterDataField: "Status",
              filterIndex: this.selectedTabId === "all" ? 1 : undefined, // Status filter shown only for the 'All' tab
              cellTemplate: "Status",
              dataSourceConfig: {
                data: [
                  { key: "D", value: "Draft" },
                  { key: "P", value: "Planned" },
                  { key: "B", value: "Booked" },
                  { key: "S", value: "Despatched" },
                  { key: "C", value: "Cancelled" },
                ],
              },
              valueExpr: "key",
              displayExpr: "value",
              //   minWidth: ColumnWidth.Status.minWidth,
              //   width: ColumnWidth.Status.width,
            },
            {
              screenField: "GridRecordEmail",
              cssClass: "padding-7",
              ReferenceNoField: "Code",
            },
            {
              screenField: "GridRecordPDFView",
              cssClass: "padding-7",
            },
            {
              screenField: "GridRecordPrint",
              cssClass: "padding-7",
              ReferenceNoField: "Code",
            },
          ],
        },
      };
    },

    getPersistenceKey() {
      return `distribution-manifest-list-${this.selectedTabId}`;
    },
  },
  methods: {
    async addNewManifest() {
      this.$router.push({
        path: `/distribution-manifest/create`,
      });
    },

    rowClickCallBack(e) {
      if (
        e.rowType == "data" &&
        e.column.screenField != "Status" &&
        e.column.screenField != "GridRecordPDFView" &&
        e.column.screenField != "GridRecordEmail" &&
        e.column.screenField != "GridRecordPrint"
      ) {
        this.onRowClicked(e, {
          view: this,
          page: "distribution-manifest",
          mode: "review",
          idProperty: "RecordId",
        });
      }
    },
  },

  created() {
    const tabParam = this.$route.query.tab;
    if (
      tabParam &&
      DistributionManifestListTabs().find((t) => t.id === tabParam)
    ) {
      this.selectedTabId = tabParam;
    }
  },
  watch: {
    "$route.query.tab": function (newTab) {
      if (
        newTab &&
        DistributionManifestListTabs().find((t) => t.id === newTab)
      ) {
        this.selectedTabId = newTab;
      } else {
        this.selectedTabId = "all";
      }

      this.$nextTick(() => {
        // this.$refs["distribution-manifest-list"].updateGridViewWithFilters();
        // Introduced a key to force re-rendering of the TransactionListTemplate component when the tab changes,
        // ensuring that the grid updates correctly with the new filters.
        // & to reflect the 'ScreenName' change in the grid header when the tab changes.
        this.listKey++;
      });
    },
  },
};
</script>
